import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE } from '../config';
import { api } from '../api';
import { useTheme } from '../context/ThemeContext';
import { useSocket } from '../context/SocketContext';
import { useAlert } from '../hooks/useAlert';
import { useAuth } from '../context/AuthContext';
import { useGuestGate } from '../context/GuestGateContext';
import { SkeletonForm } from '../components/Skeleton';
import {
  PageHeader, SectionLabel, Chip, AmountChip, SummaryCard, SummaryRow, PayButton, PleaseWait, Dropdown,
} from '../components/Form';
import Modal from '../components/Modal';
import Button from '../components/Button';
import Icon from '../components/Icon';
import FirstDepositBonusBanner from '../components/FirstDepositBonusBanner';

const AMOUNT_OPTIONS = [5, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
// Card (TheSolar) gateway funds a game directly with a $10 floor.
const CARD_MIN_AMOUNT = 10;
// The card processor skims 10%; the app compensates with a matching 10% bonus,
// so a $10 card deposit loads $11 of game credit.
const CARD_FEE_COMP_RATE = 0.1;
const fmtMoney = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2));

interface Game { id: string; name: string; credentialRequestsEnabled?: boolean; platform?: string | null; }
interface PaymentMethod { id: string; name: string; paymentTag: string | null; logoUrl: string | null; gateway?: string | null; }
interface PaymentAccount { id: string; label: string; deepLink: string; }

const WALLET_OPTION = { id: '__wallet__', name: 'Wallet', paymentTag: null, logoUrl: null } as PaymentMethod;

function allowsCredentialRequests(game: Game) {
  return game.credentialRequestsEnabled !== false;
}

export default function TopUp() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { orderUpdateCount } = useSocket();
  const { alert } = useAlert();
  const { isGuest } = useAuth();
  const { requireAuth } = useGuestGate();
  const [games, setGames] = useState<Game[]>([]);
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(WALLET_OPTION);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPleaseWait, setShowPleaseWait] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [payAccount, setPayAccount] = useState<PaymentAccount | null>(null);
  const [showGamePicker, setShowGamePicker] = useState(false);
  const [walletError, setWalletError] = useState('');
  // Persona KYC verification link returned by TheSolar on a gated card deposit.
  // Non-null => show the "provider requires verification" modal.
  const [kycLink, setKycLink] = useState<string | null>(null);

  // Live wallet balance — refresh on mount, on order updates (socket), and when
  // the tab regains focus/visibility so the displayed balance stays current.
  const refreshWallet = useCallback(async () => {
    if (isGuest) return; // no wallet to read while browsing
    try {
      const res = await api.get<{ balance: number }>('/wallet/me');
      setWalletBalance(res.data.balance || 0);
    } catch { /* best-effort */ }
  }, [isGuest]);

  useEffect(() => {
    (async () => {
      try {
        // Game top-ups can be funded from the wallet, a manual deep-link method
        // (CashApp/Chime), or a card gateway — all are offered here.
        const [gamesRes, methodsRes] = await Promise.all([
          axios.get<Game[]>(`${API_BASE}/games`),
          axios.get<PaymentMethod[]>(`${API_BASE}/payment-methods`),
        ]);
        setGames(gamesRes.data.filter(allowsCredentialRequests));
        setMethods(methodsRes.data);
      } catch {
        alert(t('topup.errorTitle'), t('topup.prepareFailed'));
      } finally {
        setLoading(false);
      }
    })();
  }, [alert, t]);

  useEffect(() => {
    refreshWallet();
    const onVisible = () => { if (document.visibilityState === 'visible') refreshWallet(); };
    const onFocus = () => refreshWallet();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshWallet, orderUpdateCount]);

  const isWallet = selectedMethod?.id === WALLET_OPTION.id;
  const isCard = selectedMethod?.gateway === 'thesolar';

  const handlePayNow = async () => {
    if (!selectedGame) return alert(t('topup.missingInfoTitle'), t('topup.selectGameMessage'));
    if (!selectedMethod) return alert(t('topup.missingInfoTitle'), t('topup.selectMethodMessage'));
    if (!selectedAmount) return alert(t('topup.selectAmountTitle'), t('topup.selectAmountMessage'));
    if (isCard && selectedAmount < CARD_MIN_AMOUNT) return alert(t('topup.selectAmountTitle'), t('topup.cardMinMessage'));

    if (isCard) return handleCardDeposit();

    if (isWallet) {
      const balanceCents = Math.round(walletBalance * 100);
      const amountCents = Math.round(selectedAmount * 100);
      if (balanceCents < amountCents) {
        setWalletError(t('topup.walletHasPickSmaller', { balance: walletBalance.toFixed(2) }));
        return;
      }
      setWalletError('');
      setSubmitting(true);
      const previousBalance = walletBalance;
      setWalletBalance(b => Math.round(b * 100 - amountCents) / 100);
      try {
        // Wallet -> game load. One endpoint for BOTH automated and manual games:
        // /orders/topup opens the order at Verified (wallet already debited). The
        // backend then auto-loads it onto the panel in the background for connected
        // games (§6b), or leaves it for a staff load on manual games — the app
        // doesn't need to branch. (The old /orders/load-game synchronous route was
        // never built and 404'd the moment a game got a platform set.)
        await api.post('/orders/topup', {
          gameId: selectedGame.id,
          amount: selectedAmount,
          target: 'game',
          paymentSource: 'wallet',
        });
        api.get<{ balance: number }>('/wallet/me')
          .then(res => setWalletBalance(res.data.balance || 0))
          .catch(() => null);
        setSelectedAmount(null);
        setShowPleaseWait(true);
        setTimeout(() => {
          setShowPleaseWait(false);
          navigate('/profile?openHistory=1');
        }, 2200);
      } catch (err: any) {
        setWalletBalance(previousBalance);
        const data = err?.response?.data;
        const message = data?.code === 'INSUFFICIENT_WALLET_BALANCE'
          ? t('topup.walletHasTopUpFirst', { balance: Number(data?.balance || 0).toFixed(2) })
          : data?.message || t('topup.submitFailed');
        if (data?.code === 'INSUFFICIENT_WALLET_BALANCE') setWalletError(message);
        else alert(t('topup.orderErrorTitle'), message);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.get<PaymentAccount>(`/payment-methods/${selectedMethod.id}/random-account`);
      setPayAccount(res.data);
      setShowPayModal(true);
    } catch (err: any) {
      alert(t('topup.noPaymentAccountTitle'), err?.response?.data?.message || t('topup.noPaymentAccountMessage'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmPay = async () => {
    if (!payAccount || !selectedGame || !selectedMethod || !selectedAmount) return;
    setShowPayModal(false);

    // Open the payment app's deep link directly inside the click gesture. A fresh,
    // user-activated navigation lets iOS/Android hand off https universal links
    // (cash.app, app.chime.com) to the native app — a pre-opened blank tab navigated
    // via location.href after the await below does NOT. Being inside the gesture also
    // keeps the popup-blocker from killing it.
    try { window.open(payAccount.deepLink, '_blank', 'noopener'); } catch { /* ignore */ }

    try {
      await api.post('/orders/topup', {
        gameId: selectedGame.id,
        paymentMethodId: selectedMethod.id,
        paymentAccountId: payAccount.id,
        amount: selectedAmount,
        target: 'game',
        paymentSource: 'method',
      });
    } catch (err: any) {
      alert(t('topup.orderErrorTitle'), err?.response?.data?.message || t('topup.orderRecordFailed'));
      return;
    }

    setSelectedAmount(null);
    setPayAccount(null);
    setShowPleaseWait(true);
    setTimeout(() => {
      setShowPleaseWait(false);
      navigate('/profile?openHistory=1');
    }, 2500);
  };

  // Card path: fund the game directly via hosted checkout. Hosted checkout pages
  // refuse to be iframed (X-Frame-Options), so we open them in a new tab. We
  // pre-open the tab inside the click gesture (popup-blocker safe), then point it
  // at the checkout link. The deposit webhook finalizes the order asynchronously.
  const handleCardDeposit = async () => {
    if (!selectedGame || !selectedMethod || !selectedAmount) return;
    const checkoutWindow = window.open('', '_blank');
    setSubmitting(true);
    try {
      const res = await api.post<{ orderId: string; orderNumber: string; paymentLink: string }>('/orders/thesolar/deposit', {
        paymentMethodId: selectedMethod.id,
        amount: selectedAmount,
        target: 'game',
        gameId: selectedGame.id,
      });
      const link = res.data?.paymentLink;
      if (!link) throw new Error('no link');
      if (checkoutWindow) checkoutWindow.location.href = link;
      else window.location.href = link;
    } catch (err: any) {
      if (checkoutWindow) { try { checkoutWindow.close(); } catch { /* ignore */ } }
      // TheSolar (inquiry KYC flow) can reject the deposit with a `kycLink` —
      // a Persona hosted verification page. Don't silently redirect: tell the
      // player the payment provider needs to verify them, then let them choose
      // to continue. The link opens on their explicit tap (popup-safe gesture).
      const link = err?.response?.data?.kycLink;
      if (typeof link === 'string' && link) {
        setKycLink(link);
        setSubmitting(false);
        return;
      }
      alert(t('topup.orderErrorTitle'), err?.response?.data?.message || t('topup.orderRecordFailed'));
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    setSelectedAmount(null);
    setShowPleaseWait(true);
    setTimeout(() => {
      setShowPleaseWait(false);
      navigate('/profile?openHistory=1');
    }, 2500);
  };

  // Wallet + all configured methods (manual deep-link + card gateway).
  const methodOptions: PaymentMethod[] = [WALLET_OPTION, ...methods];
  const amountOptions = isCard ? AMOUNT_OPTIONS.filter(a => a >= CARD_MIN_AMOUNT) : AMOUNT_OPTIONS;
  const canPay = !!selectedGame && !!selectedMethod && !!selectedAmount && (!isCard || selectedAmount >= CARD_MIN_AMOUNT);

  // Card deposits load a 10% bonus on top to offset the processor's 10% fee.
  const cardBonus = isCard && selectedAmount ? selectedAmount * CARD_FEE_COMP_RATE : 0;
  const cardCredited = isCard && selectedAmount ? selectedAmount + cardBonus : 0;

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg }}>
      <PageHeader icon="arrow-circle-up" title={t('topup.title')} />

      {loading ? (
        <div style={{ padding: 20 }}><SkeletonForm fields={4} /></div>
      ) : (
        <div data-tour="topup-form" style={{ padding: 20, paddingBottom: 40 }}>
          <FirstDepositBonusBanner variant="callout" />
          <SectionLabel>{t('topup.steps.selectGame')}</SectionLabel>
          <Dropdown
            icon="gamepad"
            text={selectedGame?.name || ''}
            placeholder={t('topup.selectGamePlaceholder')}
            onClick={() => setShowGamePicker(true)}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <SectionLabel>{t('topup.steps.paymentMethod')}</SectionLabel>
            {isWallet && (
              <div style={{
                fontSize: 11, fontWeight: 800, padding: '4px 10px',
                borderRadius: 999, color: colors.accentText,
                backgroundColor: colors.accentSoft, marginTop: 18,
              }}>
                {t('topup.balance', { balance: walletBalance.toFixed(2) })}
              </div>
            )}
          </div>

          {walletError && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              borderRadius: 14, border: `1px solid ${colors.cardBorder}`,
              backgroundColor: colors.accentSoft,
              padding: 12, marginBottom: 12,
            }}>
              <Icon name="wallet" size={14} color={colors.accentText} />
              <div style={{ flex: 1, fontSize: 12, lineHeight: 1.45, fontWeight: 600, color: colors.accentText }}>
                {walletError}
              </div>
              <button
                onClick={() => navigate('/wallet-topup')}
                style={{
                  borderRadius: 999, padding: '7px 10px', backgroundColor: colors.accent,
                  border: 'none', color: '#020617', fontSize: 11, fontWeight: 800, cursor: 'pointer',
                }}
              >
                {t('topup.topUpWallet')}
              </button>
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {methodOptions.map(m => {
              const isSelected = selectedMethod?.id === m.id;
              const isWalletPill = m.id === WALLET_OPTION.id;
              return (
                <Chip
                  key={m.id}
                  active={isSelected}
                  onClick={() => { setWalletError(''); setSelectedMethod(m); }}
                  label={m.name}
                  icon={
                    isWalletPill ? (
                      <Icon name="wallet" size={13} color={isSelected ? '#020617' : colors.accentText} />
                    ) : m.logoUrl ? (
                      <img src={m.logoUrl} alt="" style={{ width: 18, height: 18, borderRadius: 4, objectFit: 'contain' }} />
                    ) : (
                      <Icon name="credit-card" size={13} color={isSelected ? '#020617' : colors.textSub} />
                    )
                  }
                />
              );
            })}
          </div>

          <SectionLabel>{t('topup.steps.selectAmount')}</SectionLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {amountOptions.map(amt => (
              <AmountChip key={amt} amount={amt} active={selectedAmount === amt} onClick={() => setSelectedAmount(amt)} />
            ))}
          </div>
          {isCard && (
            <div style={{ fontSize: 12, color: colors.textSub, marginTop: 8 }}>
              {t('topup.cardMinHint')}
            </div>
          )}

          {canPay && (
            <SummaryCard title={t('topup.summary.title')}>
              <SummaryRow label={t('topup.summary.game')} value={selectedGame?.name || ''} />
              <SummaryRow label={t('topup.summary.paymentMethod')} value={selectedMethod?.name || ''} />
              <SummaryRow label={t('topup.summary.amount')} value={`$${selectedAmount}`} />
              {isCard && (
                <>
                  <SummaryRow label={t('topup.summary.cardBonus')} value={`+$${fmtMoney(cardBonus)}`} />
                  <SummaryRow label={t('topup.summary.gameCredit')} value={`$${fmtMoney(cardCredited)}`} />
                </>
              )}
              <SummaryRow label={t('topup.summary.processingTime')} value={isWallet ? t('topup.summary.instant') : t('topup.summary.range')} />
            </SummaryCard>
          )}

          {isCard && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 8,
              backgroundColor: colors.accentSoft, borderRadius: 12, padding: 12, marginTop: 14,
            }}>
              <Icon name="info-circle" size={13} color={colors.accentText} style={{ marginTop: 2 }} />
              <div style={{ flex: 1, fontSize: 12, lineHeight: 1.5, color: colors.accentText }}>
                {t('topup.cardFeeNotice')}
              </div>
            </div>
          )}

          <PayButton
            label={isWallet ? t('topup.payFromWallet') : isCard ? t('topup.payByCard') : t('topup.continueToPayment')}
            onClick={() => requireAuth(handlePayNow)}
            loading={submitting}
            disabled={!canPay}
          />
        </div>
      )}

      <Modal open={showPayModal} onClose={() => setShowPayModal(false)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, marginBottom: 12 }}>{t('topup.confirmPayment')}</div>
        <div style={{
          textAlign: 'center', padding: 20, borderRadius: 14,
          backgroundColor: colors.bg, border: `1px solid ${colors.cardBorder}`, marginBottom: 16,
        }}>
          <div style={{ fontSize: 36, fontWeight: 900, color: colors.text }}>${selectedAmount?.toFixed(2)}</div>
          <div style={{ fontSize: 14, color: colors.textSub, marginTop: 4 }}>{t('topup.via', { method: selectedMethod?.name })}</div>
          {payAccount && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
              <Icon name="link" size={12} color={colors.textSub} />
              <div style={{ fontSize: 13, fontWeight: 600, color: colors.textSub }}>{payAccount.label}</div>
            </div>
          )}
        </div>
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          backgroundColor: colors.accentSoft, borderRadius: 12, padding: 12, marginBottom: 20,
        }}>
          <Icon name="info-circle" size={13} color={colors.accentText} style={{ marginTop: 2 }} />
          <div style={{ flex: 1, fontSize: 12, lineHeight: 1.5, color: colors.accentText }}>
            {t('topup.payNote')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label={t('topup.cancel')} variant="outline" onClick={() => setShowPayModal(false)} />
          <Button label={t('topup.openAndPay')} icon="external-link-alt" onClick={handleConfirmPay} />
        </div>
      </Modal>

      <Modal open={!!kycLink} onClose={() => setKycLink(null)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <Icon name="shield-alt" size={30} color={colors.accentText} />
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, marginBottom: 8, textAlign: 'center' }}>
          {t('topup.kycRequiredTitle')}
        </div>
        <div style={{ fontSize: 14, lineHeight: 1.5, color: colors.textSub, marginBottom: 20, textAlign: 'center' }}>
          {t('topup.kycRequiredMessage')}
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label={t('topup.cancel')} variant="outline" onClick={() => setKycLink(null)} />
          <Button
            label={t('topup.kycVerifyButton')}
            icon="external-link-alt"
            onClick={() => {
              if (kycLink) { try { window.open(kycLink, '_blank', 'noopener'); } catch { /* ignore */ } }
              setKycLink(null);
            }}
          />
        </div>
      </Modal>

      <Modal open={showGamePicker} onClose={() => setShowGamePicker(false)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, marginBottom: 12 }}>{t('topup.selectGameTitle')}</div>
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {games.map(g => (
            <button
              key={g.id}
              onClick={() => { setSelectedGame(g); setShowGamePicker(false); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                padding: '13px 0', borderBottom: `1px solid ${colors.divider}`,
                background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                minHeight: 50,
              }}
            >
              <Icon name="gamepad" size={14} color={selectedGame?.id === g.id ? colors.accentDark : colors.textSub} />
              <div style={{
                flex: 1, fontSize: 15,
                color: selectedGame?.id === g.id ? colors.accentDark : colors.text,
                fontWeight: selectedGame?.id === g.id ? 700 : 400,
              }}>
                {g.name}
              </div>
              {selectedGame?.id === g.id && <Icon name="check" size={13} color={colors.accentDark} />}
            </button>
          ))}
        </div>
        <Button label={t('topup.cancel')} variant="outline" onClick={() => setShowGamePicker(false)} style={{ marginTop: 12 }} />
      </Modal>

      <PleaseWait
        open={showPleaseWait}
        title={t('topup.orderRecordedTitle')}
        message={isWallet ? t('topup.waitWallet') : t('topup.waitMethod')}
      />

    </div>
  );
}
